import React, { useCallback, useEffect, useRef, useState } from "react";

/** Порог, на котором решаем, что делает палец: скроллит контент или тянет шит вниз. */
const DIRECTION_LOCK_PX = 12;
/** Насколько нужно утянуть шит вниз, чтобы он закрылся. */
const DEFAULT_CLOSE_THRESHOLD_PX = 100;
/** Смещение, на котором шит становится полностью прозрачным. */
const FADE_DISTANCE_PX = 400;

interface Gesture {
    startX: number;
    startY: number;
    /** Был ли контент в самом верху в момент касания. */
    fromTop: boolean;
    /** Направление жеста уже определено. */
    decided: boolean;
    /** Жест признан «тянем шит», а не «скроллим контент». */
    dragging: boolean;
    offset: number;
}

interface UseSwipeToDismissOptions {
    isOpen: boolean;
    onDismiss: () => void;
    closeThreshold?: number;
}

interface UseSwipeToDismissResult {
    /** Вешается на сам шит (модальное окно). */
    sheetRef: (node: HTMLDivElement | null) => void;
    /** Вешается на скроллящийся контейнер внутри шита. */
    scrollRef: React.RefObject<HTMLDivElement>;
    offset: number;
    opacity: number;
    horizontalOffset: number;
    isDragging: boolean;
}

/**
 * Свайп вниз для закрытия модального окна.
 *
 * Намерение фиксируется один раз, в момент касания: если контент уже проскроллен,
 * весь жест до отпускания пальца считается скроллом и шит не двигается. Это
 * защищает от ложных закрытий, когда scrollTop обнуляется посреди жеста
 * (инерция, rubber-band на iOS).
 */
export const useSwipeToDismiss = ({
    isOpen,
    onDismiss,
    closeThreshold = DEFAULT_CLOSE_THRESHOLD_PX,
}: UseSwipeToDismissOptions): UseSwipeToDismissResult => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [offset, setOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    const gesture = useRef<Gesture | null>(null);
    const detach = useRef<(() => void) | null>(null);

    const optionsRef = useRef({ onDismiss, closeThreshold });
    optionsRef.current = { onDismiss, closeThreshold };

    useEffect(() => {
        gesture.current = null;
        setOffset(0);
        setIsDragging(false);
    }, [isOpen]);

    const sheetRef = useCallback((node: HTMLDivElement | null) => {
        if (detach.current) {
            detach.current();
            detach.current = null;
        }
        if (!node) return;

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length !== 1) {
                gesture.current = null;
                return;
            }
            const touch = e.touches[0];
            gesture.current = {
                startX: touch.clientX,
                startY: touch.clientY,
                fromTop: (scrollRef.current?.scrollTop ?? 0) <= 0,
                decided: false,
                dragging: false,
                offset: 0,
            };
        };

        const onTouchMove = (e: TouchEvent) => {
            const g = gesture.current;
            if (!g || e.touches.length !== 1) return;

            const touch = e.touches[0];
            const deltaY = touch.clientY - g.startY;
            const deltaX = touch.clientX - g.startX;

            if (!g.decided) {
                if (Math.abs(deltaY) < DIRECTION_LOCK_PX && Math.abs(deltaX) < DIRECTION_LOCK_PX) return;
                g.decided = true;
                g.dragging = g.fromTop && deltaY > 0 && deltaY > Math.abs(deltaX);
                if (g.dragging) setIsDragging(true);
            }

            if (!g.dragging) return;

            // Пока тянем шит — гасим нативный скролл и overscroll (в том числе pull-to-refresh).
            if (e.cancelable) e.preventDefault();
            g.offset = Math.max(0, deltaY);
            setOffset(g.offset);
        };

        const onTouchEnd = () => {
            const g = gesture.current;
            gesture.current = null;
            if (!g || !g.dragging) return;

            setIsDragging(false);
            setOffset(0);
            if (g.offset >= optionsRef.current.closeThreshold) {
                optionsRef.current.onDismiss();
            }
        };

        node.addEventListener("touchstart", onTouchStart, { passive: true });
        node.addEventListener("touchmove", onTouchMove, { passive: false });
        node.addEventListener("touchend", onTouchEnd);
        node.addEventListener("touchcancel", onTouchEnd);

        detach.current = () => {
            node.removeEventListener("touchstart", onTouchStart);
            node.removeEventListener("touchmove", onTouchMove);
            node.removeEventListener("touchend", onTouchEnd);
            node.removeEventListener("touchcancel", onTouchEnd);
        };
    }, []);

    return {
        sheetRef,
        scrollRef,
        offset,
        opacity: Math.max(0, 1 - offset / FADE_DISTANCE_PX),
        horizontalOffset: offset / 50,
        isDragging,
    };
};

export default useSwipeToDismiss;
